import type { CSSProperties, ReactNode } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { aiEnter } from './ai-motion-state'

// Shared motion for AI work (demo `components/ui/ai-motion.tsx`): a progress line, a scanning reel,
// a running/done step chip, and the arrival of new AI data. Violet is the AI signal; keyframes
// live in src/index.css and collapse under prefers-reduced-motion.

export type ScanItem = {
  key: string
  label: string
  detail?: string | null
  tone?: 'adverse' | 'missing' | 'confirmed'
}

const ROW = 28

/**
 * A 2px AI progress line with a travelling shine. Pass `value` (0–1) for measured progress, or
 * `from`/`to`/`seconds` to ease across a stage whose length is only known approximately.
 */
export function AiProgress({
  value,
  from = 0,
  to = 1,
  seconds = 1,
  done = false,
  className,
}: {
  value?: number
  from?: number
  to?: number
  seconds?: number
  done?: boolean
  className?: string
}) {
  const measured = value !== undefined
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative h-0.5 overflow-hidden bg-violet-100 dark:bg-violet-950',
        done && 'animate-[qb-ai-done_500ms_ease-in_400ms_both]',
        className,
      )}
    >
      <div
        className={cn(
          'absolute inset-0 origin-left overflow-hidden bg-violet-600',
          measured && 'transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
        )}
        style={
          measured
            ? { transform: `scaleX(${Math.max(0.02, Math.min(1, value))})` }
            : ({
                '--qb-ai-from': from,
                '--qb-ai-to': to,
                animation: `qb-ai-progress ${seconds}s cubic-bezier(0.16, 1, 0.3, 1) both`,
              } as CSSProperties)
        }
      >
        {done ? null : (
          <span className="absolute inset-y-0 left-0 w-1/4 animate-[qb-ai-shimmer_1.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
        )}
      </div>
    </div>
  )
}

/** A five-row window over what the AI is checking, with a band on the current item. */
export function ScanReel({ items, index, checks }: { items: ScanItem[]; index: number; checks: boolean }) {
  const visible = Math.min(5, items.length)
  // Keep the band in the middle once the list can scroll; at the ends, move the band instead.
  const offset = Math.max(0, Math.min(index - 2, items.length - visible))
  const ease = 'duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]'
  return (
    <div
      aria-hidden="true"
      className="relative overflow-hidden"
      style={{
        height: ROW * visible,
        maskImage:
          items.length > visible ? 'linear-gradient(transparent, #000 22%, #000 78%, transparent)' : undefined,
      }}
    >
      <div
        className={cn('absolute inset-x-0 top-0 rounded-md bg-violet-50 ring-1 ring-violet-200 transition-transform dark:bg-violet-950/50 dark:ring-violet-900', ease)}
        style={{ height: ROW, transform: `translateY(${(index - offset) * ROW}px)` }}
      />
      <ol className={cn('relative transition-transform', ease)} style={{ transform: `translateY(${-offset * ROW}px)` }}>
        {items.map((item, position) => (
          <li
            className={cn(
              'flex items-center gap-2 px-2.5 text-xs transition-colors duration-300',
              position === index ? 'text-foreground' : position < index ? 'text-muted-foreground' : 'text-muted-foreground/70',
            )}
            key={item.key}
            style={{ height: ROW }}
          >
            {checks && position < index ? (
              <Check className="size-3 shrink-0 text-emerald-600" />
            ) : (
              <span
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  position === index && 'animate-pulse',
                  item.tone === 'adverse'
                    ? 'bg-destructive'
                    : item.tone === 'confirmed'
                      ? 'bg-emerald-600'
                      : item.tone === 'missing'
                        ? 'bg-amber-500'
                        : position === index
                          ? 'bg-violet-600'
                          : 'bg-muted-foreground/30',
                )}
              />
            )}
            <span className={cn('min-w-0 flex-1 truncate', position === index && 'font-medium')}>{item.label}</span>
            {item.detail ? <span className="max-w-[45%] shrink-0 truncate text-muted-foreground">{item.detail}</span> : null}
          </li>
        ))}
      </ol>
    </div>
  )
}

export type AiStepStatus = 'running' | 'done'

/** One unit of AI or catalog work: spinning while it runs, then its one-line result. */
export function AiStep({ label, status, children }: { label: string; status: AiStepStatus; children?: ReactNode }) {
  return (
    <span
      className={cn(
        'relative inline-flex max-w-full items-center gap-1 overflow-hidden rounded-full border px-2 py-0.5 text-xs transition-colors duration-300',
        status === 'running'
          ? 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/50 dark:text-violet-300'
          : 'bg-card text-muted-foreground',
        aiEnter,
      )}
    >
      {status === 'running' ? (
        <Loader2 aria-hidden="true" className="size-3 shrink-0 animate-spin motion-reduce:animate-none" />
      ) : (
        <Check aria-hidden="true" className="size-3 shrink-0 text-emerald-600" />
      )}
      <span className="shrink-0 font-medium">{label}</span>
      {children ? <span className="min-w-0 truncate">{children}</span> : null}
      {status === 'running' ? (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-1/3 animate-[qb-ai-shimmer_1.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"
        />
      ) : null}
    </span>
  )
}
